/* eslint-disable max-lines, max-params, no-param-reassign, max-lines-per-function */
import { api, routes, resolveOptions } from "jiffy-api";
import { track } from "jiffy-analytics";
import { redirectTo } from "common/utils/browser";
import { objNamesToCamelCase, camelize } from "common/utils/helpers/cases";

const initialState = {
  checkoutButtons: {},
  crafterpayQuote: {},
  lastCompletedOrderPaymentMethodSubtype: "",
  mobileApplePayVersion: null,
  stripeFinancingSupported: false
};

const paymentsModule = {
  namespaced: true,

  state() {
    return { ...initialState };
  },

  mutations: {
    init(state, data) {
      const newState = objNamesToCamelCase(data);
      state.checkoutButtons = newState.checkoutButtons || state.checkoutButtons;
      state.crafterpayQuote = newState.crafterpayQuote || state.crafterpayQuote;
      state.mobileApplePayVersion = newState.mobileApplePayVersion ?? state.mobileApplePayVersion;
      state.stripeFinancingSupported =
        newState.stripeFinancingSupported ?? state.stripeFinancingSupported;
      state.lastCompletedOrderPaymentMethodSubtype =
        newState.lastCompletedOrderPaymentMethodSubtype ||
        state.lastCompletedOrderPaymentMethodSubtype;
    },
    setCrafterpayQuote(state, quote) {
      state.crafterpayQuote = quote;
    },
    setMobileApplePayVersion(state, version) {
      state.mobileApplePayVersion = version;
    },
    setStripeFinancingSupported(state, supported) {
      state.stripeFinancingSupported = supported;
    },
    setLastCompletedOrderPaymentMethodSubtype(state, subtype) {
      state.lastCompletedOrderPaymentMethodSubtype = subtype;
    }
  },

  actions: {
    async updateAddressAndRecalculate({ rootGetters, dispatch }, shippingContact) {
      const payload = {
        address: {
          zipcode: shippingContact?.postalCode,
          city: shippingContact?.locality,
          current: shippingContact?.current || false,
          country_code: shippingContact?.countryCode,
          state_code: shippingContact?.administrativeArea
        },
        use_store_credits: rootGetters.storeCredit.isUsed,
        use_specific_store_credits: rootGetters.useSpecificStoreCredits
      };

      return api.jiffyshirts.orders
        .updateApplePayAddressAndRecalculate({ order_number: rootGetters.orderNumber }, payload)
        .then(response => {
          dispatch("setCartResponse", response, { root: true });
          return response;
        })
        .catch(e => {
          dispatch("setSystemError", e, { root: true });
          throw e;
        });
    },

    updatePaypalOrder({ rootGetters, dispatch }, payload) {
      return api.jiffyshirts.orders
        .updatePaypalSummary({ order_number: rootGetters.orderNumber }, payload)
        .then(response => {
          dispatch("setCartResponse", response, { root: true });
          return response;
        })
        .catch(e => {
          dispatch("setSystemError", e, { root: true });
        });
    },

    async createPaypalOrder({ rootGetters, dispatch }, payload) {
      return api.jiffyshirts.orders
        .createPaypalOrder({ order_number: rootGetters.orderNumber }, payload)
        .then(response => {
          dispatch("setCartResponse", response, { root: true });
          return response;
        })
        .catch(e => {
          dispatch("setSystemError", e.toString(), { root: true });
        });
    },

    googleSelectShipping({ rootGetters, dispatch }, payload) {
      return api.jiffyshirts.orders
        .googlePaySummary({ order_number: rootGetters.orderNumber }, payload)
        .then(response => {
          dispatch("setCartResponse", response, { root: true });
          return response;
        })
        .catch(e => {
          dispatch("setSystemError", e, { root: true });
        });
    },

    async submitApplePayToken({ dispatch }, { nonce, payment }) {
      const { shippingContact, billingContact } = payment;

      const shippingAddress = {
        addressLine: shippingContact.addressLines,
        country: shippingContact.countryCode.toUpperCase(),
        postalCode: shippingContact.postalCode,
        recipient: `${shippingContact.givenName} ${shippingContact.familyName}`,
        region: shippingContact.administrativeArea,
        city: shippingContact.locality,
        phone: shippingContact.phoneNumber
      };

      const card = {
        name: `${billingContact.givenName} ${billingContact.familyName}`,
        address_city: billingContact.locality,
        address_country: billingContact.countryCode.toUpperCase(),
        address_line1: billingContact.addressLines[0],
        address_line2: billingContact.addressLines[1],
        address_state: billingContact.administrativeArea,
        address_zip: billingContact.postalCode
      };

      const payload = {
        checkout_no_confirmation: true,
        shipping: {
          fullname: `${shippingContact.givenName} ${shippingContact.familyName}`
        },
        billing: {
          fullname: `${billingContact.givenName} ${billingContact.familyName}`
        },
        order: {
          ship_address_attributes: {},
          bill_address_attributes: {}
        },
        payment_method_subtype: "apple-pay",
        payment_token: nonce,
        state: "payment",
        payment_method: "device_pay",
        payment_type: "device_pay",
        checkout: true,
        "device-pay-token": JSON.stringify({
          id: nonce,
          email: billingContact.emailAddress || shippingContact.emailAddress,
          phone: billingContact.phoneNumber || shippingContact.phoneNumber,
          card,
          shippingAddress,
          type: "card",
          paymentMethod: "apple-pay"
        })
      };

      try {
        const response = await api.jiffyshirts.checkout.submitPayment(payload);
        window.location.href = response?.redirect_to || "/cart";
      } catch (e) {
        dispatch("setSystemError", e, { root: true });
      }
    },

    async stripeCreatePaymentIntent(
      { dispatch },
      { stripe, elements, paymentMethod, paymentData }
    ) {
      try {
        const response = await api.jiffyshirts.checkout.stripeCreateIntent(paymentData);
        const { error: backendError, redirect_to: redirectUrl } = response;

        if (redirectUrl) {
          redirectTo(redirectUrl);
        } else if (backendError) {
          throw backendError;
        }

        elements.update({ customerSessionClientSecret: response.client_secret });
        await dispatch("stripeConfirmPayment", {
          stripe,
          elements,
          clientSecret: response.client_secret,
          returnUrl: response.return_url
        });
      } catch (error) {
        dispatch("expressCheckoutHandleError", { error, paymentMethod });
      } finally {
        document.body.classList.remove("processing-visible");
      }
    },

    async stripeConfirmPayment(_, { stripe, elements, clientSecret, returnUrl }) {
      const response = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: { return_url: returnUrl },
        redirect: "always"
      });

      if (response.error) {
        throw response.error;
      }

      return response;
    },

    expressCheckoutHandleError(
      { dispatch, rootGetters },
      { error, paymentMethod, redirectUrl = null }
    ) {
      dispatch("logCheckoutData", { data: error, kind: `${paymentMethod}.checkout_error` });

      window.jiffy.ui.cart.form.loading = false;
      document.body.classList.remove("processing-visible");

      dispatch("resetDeliveryAddress");
      dispatch("raiseErrorTracking", { error, paymentMethod });
      const cartUrl = rootGetters.isUs ? "/cart" : "/ca/cart";
      redirectTo(redirectUrl || cartUrl);
    },

    logCheckoutData(_, { data, kind }) {
      const loggerPayload = { order_number: window.jiffy.orderNumber, data, kind };

      try {
        api.jiffyshirts.checkout.log(loggerPayload);
      } catch (e) {
        // Silent fail
      }
    },

    resetDeliveryAddress({ commit, dispatch, rootGetters }) {
      const { addressSelected, addresses } = rootGetters;
      const origAddress = addressSelected || addresses[0];
      if (origAddress) {
        commit("changeAddress", { id: origAddress.id }, { root: true });
      } else {
        dispatch("shipping/getAllDeliveryDates", null, { root: true });
      }
      if (window.jiffy?.ui?.cart?.form) {
        window.jiffy.ui.cart.form.loading = false;
      }
    },

    raiseErrorTracking(_, { error, paymentMethod }) {
      if (window.Honeybadger) {
        window.Honeybadger.notify(`${paymentMethod} Checkout Error`, {
          params: { error: JSON.stringify(error) }
        });
      }

      track("checkout_error", {
        error_message: JSON.stringify(error),
        payment_method: paymentMethod,
        error_details: error
      });
    },

    async redirectToEditCrafterpayQuote({ rootState, rootGetters, commit, dispatch }) {
      commit("setCheckoutLoadingStatus", true, { root: true });
      commit("setInventoryLoadingStatus", true, { root: true });

      try {
        let response;
        response = await api.crafterpay.crafters.createQuote();

        const quoteId = response.quote.id;
        commit("setIsCrafterOnboarded", response.is_onboarded, { root: true });
        track("crafterpay.create_invoice_button.clicked", { quoteId });

        response = await api.crafterpay.crafters.updateQuoteShippingCost(
          { quote_id: quoteId },
          { shipping_cost: rootGetters.deliveryTotal }
        );
        if (response.success === true) {
          let url;
          const onboarded = rootState.cartState.cartConfig.isCrafterOnboarded;
          if (onboarded) {
            const editQuotePath = resolveOptions(routes.crafterpayEditQuotePath(), false, {
              quote_id: quoteId
            });
            url = editQuotePath.url;
          } else {
            url = routes.crafterpayOnboardingPath();
          }
          window.location.href = url;
        }
      } catch (e) {
        commit("setCheckoutLoadingStatus", false, { root: true });
        dispatch("setSystemError", e, { root: true });
      }
    },

    proceedCheckout({ commit }) {
      setTimeout(() => {
        commit("setCheckoutLoadingStatus", true, { root: true });
        commit("setInventoryLoadingStatus", true, { root: true });
      }, 0);
    }
  },

  getters: {
    // Checkout buttons
    checkoutButtons: state => state.checkoutButtons,
    androidGpayButtonEnabled: (state, getters) => getters.checkoutButtons.androidGpayButtonEnabled,
    iosApplePayButtonEnabled: (state, getters) => getters.checkoutButtons.iosApplePayButtonEnabled,
    googlePayButtonEnabled: (state, getters) => getters.checkoutButtons.googlePayButtonEnabled,
    googlePayStripeButtonEnabled: (state, getters) =>
      getters.checkoutButtons.googlePayStripeButtonEnabled,
    applePayStripeButtonEnabled: (state, getters) =>
      getters.checkoutButtons.applePayStripeButtonEnabled,
    googlePayLogo: (state, getters) => getters.checkoutButtons.googlePayLogo,
    affirmButtonEnabled: (state, getters) => getters.checkoutButtons.affirmButtonEnabled,
    afterpayButtonEnabled: (state, getters) => getters.checkoutButtons.afterpayButtonEnabled,
    sezzleButtonEnabled: (state, getters) => getters.checkoutButtons.sezzleButtonEnabled,
    nativePaypalCheckoutEnabled: (state, getters) =>
      getters.checkoutButtons.nativePaypalCheckoutEnabled,

    // Crafterpay
    isCrafterpayOn: (_state, _getters, rootState) =>
      rootState.cartState.cartConfig.crafterpayAvailable,
    hasCrafterpayQuote: state => !!state.crafterpayQuote.id,
    hasActiveCrafterpayQuote: state => {
      const quoteState = state.crafterpayQuote.state;
      return quoteState && quoteState !== "deactivated";
    },
    crafterpayQuote: state => state.crafterpayQuote,

    // Payment method info
    mobileApplePayVersion: state => state.mobileApplePayVersion,
    stripeFinancingSupported: state => state.stripeFinancingSupported,
    lastCompletedOrderPaymentMethodSubtype: state =>
      state.lastCompletedOrderPaymentMethodSubtype || "custom",

    // Payment payloads
    standardApplePayPayload: (_state, _getters, _rootState, rootGetters) =>
      rootGetters["shipping/standardShippingOptions"].map(option => {
        if (!option) return {};

        const cost =
          rootGetters.couponCode.freeShipping || option.free
            ? option.surcharge
            : option.cost + option.surcharge;
        const deliveryDates = [
          ...new Set(
            rootGetters["shipping/sortedStandardShippingOptions"](option).map(
              shippingOption =>
                ` - ${
                  shippingOption?.formattedDeliveryDate || shippingOption?.formattedArrivalDate
                }`
            )
          )
        ];

        return {
          id: option.id,
          identifier: option.category,
          label: option.title || "Shipping",
          detail:
            deliveryDates.length > 0 ? `Estimated Arrival: \n${deliveryDates.join("\n")}` : "",
          amount: cost,
          selected: option.selected
        };
      }),

    mcuApplePayPayload: (_state, getters, _rootState, rootGetters) => {
      const mcuShipmentsOnly = !rootGetters["shipping/selectedShipments"][0];

      if (mcuShipmentsOnly) {
        return Object.keys(rootGetters["shipping/deliveryDates"]).map(category => {
          const mcuShipments = rootGetters["shipping/deliveryDates"][category];
          const deliveryDates = [
            ...new Set(
              mcuShipments.map(({ formattedDeliveryDate }) => ` - ${formattedDeliveryDate}`)
            )
          ];
          const cost = rootGetters.couponCode.freeShipping
            ? 0
            : rootGetters["shipping/mcuShippingCost"](category);

          return {
            identifier: category,
            label: rootGetters["shipping/shipments"].titles[camelize(category)],
            detail:
              mcuShipments.length > 0 ? `Estimated Arrival: \n${deliveryDates.join("\n")}` : "",
            amount: cost,
            selected:
              category === (rootGetters["shipping/mcuOnlySelectedOption"]?.category || "fastest")
          };
        });
      }

      if (rootGetters.couponCode.freeShippingPromotionType === "all") {
        return getters.standardApplePayPayload;
      }

      return getters.standardApplePayPayload.map(option => ({
        ...option,
        amount:
          parseFloat(option.amount) + rootGetters["shipping/mcuShippingCost"](option.identifier)
      }));
    },

    applePayPayload: (_state, getters, _rootState, rootGetters) => {
      const requiredAddressFields = ["email", "name", "phone", "postalAddress"];
      const {
        countryCode,
        currencyCode,
        cartTotal,
        shippingTotal,
        taxTotal,
        isMcuCart,
        processingFee
      } = rootGetters;

      const { amount: storeCreditAmount, isUsed: useStoreCredits } = rootGetters.storeCredit;

      let lineItems = [
        {
          label: "Item Total",
          type: "final",
          amount: cartTotal - shippingTotal - taxTotal - processingFee
        },
        {
          label: "Shipping",
          type: "final",
          amount: shippingTotal
        },
        {
          label: "Tax Total",
          type: "final",
          amount: taxTotal
        }
      ];
      let total = cartTotal;

      if (processingFee > 0) {
        lineItems = [
          ...lineItems,
          {
            label: "Processing Fee",
            type: "final",
            amount: processingFee
          }
        ];
      }

      Object.values(rootGetters.specificStoreCredits).some(({ isUsed, amount }) => {
        if (isUsed) {
          const amountUsed = Math.min(amount, total);
          lineItems = [
            ...lineItems,
            {
              label: "Store Credit",
              type: "final",
              amount: -amountUsed
            }
          ];

          total -= amountUsed;
        }

        return total === 0;
      });

      if (total > 0 && useStoreCredits) {
        const storeCreditUsed = Math.min(storeCreditAmount, total);
        lineItems = [
          ...lineItems,
          {
            label: "Store Credit",
            type: "final",
            amount: -storeCreditUsed
          }
        ];

        total -= storeCreditUsed;
      }

      const stripeLineItems = lineItems.map(item => ({
        name: item.label,
        amount: Math.round(item.amount * 100)
      }));

      return {
        countryCode,
        currencyCode,
        total: {
          label: "Total",
          type: "final",
          amount: total
        },
        lineItems,
        stripeLineItems,
        stripeTotalAmount: Math.round(total * 100),
        requiredBillingContactFields: requiredAddressFields,
        requiredShippingContactFields: requiredAddressFields,
        shippingMethods: isMcuCart ? getters.mcuApplePayPayload : getters.standardApplePayPayload
      };
    },

    paypalPayload: (_state, _getters, _rootState, rootGetters) => {
      const {
        env,
        host,
        addressSelected,
        orderNumber,
        currencyCode,
        cartTotal,
        cartSummary,
        shippingTotal,
        taxTotal,
        hasCouponApplied,
        couponCode,
        processingFee
      } = rootGetters;

      const surchargeTotal = rootGetters["shipping/surchargeTotal"];
      const { amount: storeCreditAmount, isUsed: useStoreCredits } = rootGetters.storeCredit;
      const finalShippingCost = Math.max(shippingTotal - surchargeTotal, 0);

      let amountTotal = cartTotal;
      let itemTotal = parseFloat(cartSummary.total);
      let discountTotal = 0;

      Object.values(rootGetters.specificStoreCredits).some(({ isUsed, amount }) => {
        if (isUsed) {
          const amountUsed = Math.min(amount, amountTotal);
          amountTotal -= amountUsed;
          discountTotal += amountUsed;
        }

        return amountTotal === 0;
      });

      if (amountTotal > 0 && useStoreCredits) {
        const storeCreditUsed = Math.min(storeCreditAmount, amountTotal);
        amountTotal -= storeCreditUsed;
        discountTotal += storeCreditUsed;
      }

      if (hasCouponApplied && !couponCode.freeShipping) {
        itemTotal += parseFloat(couponCode.couponDiscount);
        discountTotal += parseFloat(couponCode.couponDiscount);
      }

      // Add server name to keep the invoice_id unique even when used in different stagings
      const invoiceID =
        env !== "production"
          ? `${host}-${new Date().toJSON().slice(0, 10)}-${orderNumber}`
          : orderNumber;

      const payload = {
        invoice_id: invoiceID,
        amount: {
          value: String(amountTotal.toFixed(2)),
          currency_code: currencyCode,
          breakdown: {
            item_total: {
              value: String(itemTotal.toFixed(2)),
              currency_code: currencyCode
            },
            tax_total: {
              value: String(taxTotal.toFixed(2)),
              currency_code: currencyCode
            },
            discount: {
              value: String(discountTotal.toFixed(2)),
              currency_code: currencyCode
            },
            shipping: {
              value: String(finalShippingCost.toFixed(2)),
              currency_code: currencyCode
            },
            handling: {
              value: String((surchargeTotal + processingFee).toFixed(2)),
              currency_code: currencyCode
            }
          }
        }
      };

      if (addressSelected) {
        payload.shipping = {
          address: {
            address_line_1: addressSelected.address1,
            address_line_2: addressSelected.address2,
            admin_area_2: addressSelected.city,
            admin_area_1: addressSelected.state,
            postal_code: addressSelected.zipcode,
            country_code: addressSelected.country
          }
        };
      } else {
        payload.shipping = { options: rootGetters["shipping/paypalShippingOptions"] };
      }

      return payload;
    },

    stripeLineItems: (_state, _getters, _rootState, rootGetters) => category => {
      const { amount: storeCreditAmount, isUsed: useStoreCredits } = rootGetters.storeCredit;
      const { freeShipping, couponDiscount, freeShippingPromotionType } = rootGetters.couponCode;

      const {
        cartTotal,
        cartBulkDiscount,
        hasCouponApplied,
        taxTotal,
        processingFee,
        shippingTotal
      } = rootGetters;
      const deliveryTotal = rootGetters["shipping/deliveryTotal"];
      const surchargeTotal = rootGetters["shipping/surchargeTotal"];
      const selectedShipments = rootGetters["shipping/selectedShipments"];
      const shippingDiscount = rootGetters["shipping/shippingDiscount"];

      let lineItems = [
        {
          name: "Product Total",
          amount: Math.round((cartTotal - shippingTotal - taxTotal - processingFee) * 100)
        }
      ];

      if (cartBulkDiscount > 0) {
        lineItems = [
          ...lineItems,
          {
            name: "Bulk Discount",
            amount: Math.round(-cartBulkDiscount * 100)
          }
        ];
      }

      if (processingFee > 0) {
        lineItems = [
          ...lineItems,
          {
            name: "Processing Fee",
            amount: Math.round(processingFee * 100)
          }
        ];
      }

      if (hasCouponApplied && !freeShipping) {
        lineItems = [
          ...lineItems,
          {
            name: "Discount Coupon",
            amount: Math.round(-couponDiscount * 100)
          }
        ];
      }

      const shipTotal = parseFloat(deliveryTotal + shippingDiscount);
      lineItems = [
        ...lineItems,
        {
          name: "Shipping",
          amount: Math.round(shipTotal * 100)
        }
      ];

      if (shippingDiscount > 0) {
        lineItems = [
          ...lineItems,
          {
            name: "Shipping Discount",
            amount: Math.round(-shippingDiscount * 100)
          }
        ];
      }

      if (hasCouponApplied && freeShipping) {
        let shippingCouponDiscount = couponDiscount;
        if (category && freeShippingPromotionType === "single") {
          const shipment = Object.values(selectedShipments).find(
            option => camelize(option.category) === category
          );
          shippingCouponDiscount = shipment.strikedShipPrice;
        }
        lineItems = [
          ...lineItems,
          {
            name: "Discount Coupon",
            amount: Math.round(-shippingCouponDiscount * 100)
          }
        ];
      }

      if (surchargeTotal > 0) {
        lineItems = [
          ...lineItems,
          {
            name: "Fuel Surcharge",
            amount: Math.round(surchargeTotal * 100)
          }
        ];
      }

      if (taxTotal > 0) {
        lineItems = [
          ...lineItems,
          {
            name: "Tax Total",
            amount: Math.round(taxTotal * 100)
          }
        ];
      }

      let total = cartTotal;
      Object.values(rootGetters.specificStoreCredits).some(({ isUsed, amount }) => {
        if (isUsed) {
          const amountUsed = Math.min(parseFloat(amount), total);
          lineItems = [
            ...lineItems,
            {
              name: "Store Credits",
              amount: Math.round(-amountUsed * 100)
            }
          ];

          total -= amountUsed;
        }

        return total === 0;
      });

      if (total > 0 && useStoreCredits) {
        const storeCreditUsed = Math.min(storeCreditAmount, total);
        lineItems = [
          ...lineItems,
          {
            name: "Store Credits",
            amount: Math.round(-storeCreditUsed * 100)
          }
        ];
      }

      return lineItems;
    },

    stripeTotal: (_state, _getters, _rootState, rootGetters) => {
      let total = rootGetters.cartTotal;
      const { amount: storeCreditAmount, isUsed: useStoreCredits } = rootGetters.storeCredit;
      Object.values(rootGetters.specificStoreCredits).some(({ isUsed, amount }) => {
        if (isUsed) {
          const amountUsed = Math.min(parseFloat(amount), total);
          total -= amountUsed;
        }

        return total === 0;
      });

      if (total > 0 && useStoreCredits) {
        const storeCreditUsed = Math.min(storeCreditAmount, total);
        total -= storeCreditUsed;
      }

      return {
        amount: Math.round(total * 100),
        currency: rootGetters.currencyCode.toLowerCase()
      };
    },

    mcuStripePayload: (_state, getters, _rootState, rootGetters) => {
      const { couponCode } = rootGetters;

      const selectedShipments = rootGetters["shipping/selectedShipments"];
      const deliveryDates = rootGetters["shipping/deliveryDates"];
      const shipments = rootGetters["shipping/shipments"];

      const { freeShipping, freeShippingPromotionType } = couponCode;
      const mcuShipmentsOnly = !selectedShipments[0];

      if (mcuShipmentsOnly) {
        return Object.keys(deliveryDates).map(category => {
          const mcuShipments = deliveryDates[category];
          const shippingOptionsDates = [
            ...new Set(mcuShipments.map(({ formattedDeliveryDate }) => formattedDeliveryDate))
          ];
          const dates = [
            ...new Set([
              shippingOptionsDates[0],
              shippingOptionsDates[shippingOptionsDates.length - 1]
            ])
          ];
          const cost = freeShipping ? 0 : rootGetters["shipping/mcuShippingCost"](category);

          return {
            identifier: camelize(category),
            label: shipments.titles[camelize(category)] || "Shipping",
            detail: mcuShipments.length > 0 ? `Estimated Arrival: \n${dates.join(" - ")}` : "",
            amount: cost,
            surcharge: 0,
            selected:
              category === (rootGetters["shipping/mcuOnlySelectedOption"]?.category || "fastest"),
            deliveryEstimate:
              mcuShipments.length > 0 ? `Estimated Arrival: \n${dates.join(" - ")}` : ""
          };
        });
      }

      if (freeShippingPromotionType === "all") {
        return getters.standardStripePayload;
      }

      return getters.standardStripePayload.map(option => ({
        ...option,
        amount:
          parseFloat(option.amount) + rootGetters["shipping/mcuShippingCost"](option.identifier)
      }));
    },

    standardStripePayload: (_state, _getters, _rootState, rootGetters) => {
      const { couponCode } = rootGetters;
      const standardShippingOptions = rootGetters["shipping/standardShippingOptions"];
      const { freeShipping } = couponCode;

      return standardShippingOptions.map(option => {
        if (!option) return {};

        const cost =
          freeShipping || option.free ? option.surcharge : option.cost + option.surcharge;
        const deliveryDates = [
          ...new Set(
            rootGetters["shipping/sortedStandardShippingOptions"](option).map(
              shippingOption =>
                ` - ${
                  shippingOption?.formattedDeliveryDate || shippingOption?.formattedArrivalDate
                }`
            )
          )
        ];

        return {
          identifier: camelize(option.category),
          label: option.title || "Shipping",
          amount: cost,
          cost: option.cost,
          surcharge: option.surcharge,
          selected: option.selected,
          deliveryEstimate:
            deliveryDates.length > 0 ? `Estimated Arrival: ${deliveryDates.join(" - ")}` : ""
        };
      });
    }
  }
};

export default paymentsModule;
